import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useScript } from '../context/ScriptContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useLangFade } from '../hooks/useLangFade';

/* ── Before / After pairs ─────────────────────────────────────── */
interface FrescoPair {
  id: string;
  title: string;
  titleLat: string;
  before: string;
  after: string;
  description?: string;
  descriptionLat?: string;
}

/* Pair images live in poravnato/: each "before" photo was warped onto its
   "after" photo (feature matching + homography) and both were cropped to
   their common area, so the slider overlays line up. */
const FRESCO_PAIRS: FrescoPair[] = [
  {
    id: 'ktitorska',
    title: 'Ктиторска композиција',
    titleLat: 'Ktitorska kompozicija',
    before: '/resource/freskopis/poravnato/ktitorska-pre.webp',
    after: '/resource/freskopis/poravnato/ktitorska-posle.webp',
    description: 'Пресвета Богородица и свети краљ Стефан Првовенчани заступају ктиторски пар пред Господом Исусом Христом.',
    descriptionLat: 'Presveta Bogorodica i sveti kralj Stefan Prvovenčani zastupaju ktitorski par pred Gospodom Isusom Hristom.',
  },
  {
    id: 'uspenje',
    title: 'Успење Пресвете Богородице',
    titleLat: 'Uspenje Presvete Bogorodice',
    before: '/resource/freskopis/poravnato/uspenje-pre.webp',
    after: '/resource/freskopis/poravnato/uspenje-posle.webp',
    description: 'Христос прихвата Богородицу као тек рођену бебу у своје наручје.',
    descriptionLat: 'Hristos prihvata Bogorodicu kao tek rođenu bebu u svoje naručje.',
  },
  {
    id: 'kupanje',
    title: 'Купање Богомладенца',
    titleLat: 'Kupanje Bogomladenca',
    before: '/resource/freskopis/poravnato/kupanje-pre.webp',
    after: '/resource/freskopis/poravnato/kupanje-posle.webp',
  },
  {
    id: 'pastiri',
    title: 'Композиција са пастирима',
    titleLat: 'Kompozicija sa pastirima',
    before: '/resource/freskopis/poravnato/pastiri-pre.webp',
    after: '/resource/freskopis/poravnato/pastiri-posle.webp',
  },
  {
    id: 'gedeon',
    title: 'Свети пророк Гедеон',
    titleLat: 'Sveti prorok Gedeon',
    before: '/resource/freskopis/poravnato/gedeon-pre.webp',
    after: '/resource/freskopis/poravnato/gedeon-posle.webp',
    description: 'На златној позадини исцртани су квадратићи како би се постигао изглед мозаика.',
    descriptionLat: 'Na zlatnoj pozadini iscrtani su kvadratići kako bi se postigao izgled mozaika.',
  },
  {
    id: 'obraz',
    title: 'Нерукотворени образ',
    titleLat: 'Nerukotvoreni obraz',
    before: '/resource/freskopis/poravnato/obraz-pre.webp',
    after: '/resource/freskopis/poravnato/obraz-posle.webp',
  },
  {
    id: 'jevandjelist',
    title: 'Јеванђелиста',
    titleLat: 'Jevanđelista',
    before: '/resource/freskopis/poravnato/jevandjelist-pre.webp',
    after: '/resource/freskopis/poravnato/jevandjelist-posle.webp',
  },
  {
    id: 'svetitelj',
    title: 'Непознати светитељ',
    titleLat: 'Nepoznati svetitelj',
    before: '/resource/freskopis/poravnato/svetitelj-pre.webp',
    after: '/resource/freskopis/poravnato/svetitelj-posle.webp',
  },
  {
    id: 'ktitorska-detalj-2',
    title: 'Ктиторска композиција — детаљ фреске',
    titleLat: 'Ktitorska kompozicija — detalj freske',
    before: '/resource/freskopis/poravnato/ornament-pre.webp',
    after: '/resource/freskopis/poravnato/ornament-posle.webp',
  },
  {
    id: 'raspece',
    title: 'Распеће',
    titleLat: 'Raspeće',
    before: '/resource/freskopis/poravnato/raspece-pre.webp',
    after: '/resource/freskopis/poravnato/raspece-posle.webp',
    description: 'Монументална сцена Распећа на западном зиду наоса.',
    descriptionLat: 'Monumentalna scena Raspeća na zapadnom zidu naosa.',
  },
];

/* ── Extra images that don't have a pair (only before or only after) ── */
interface FrescoSingle {
  id: string;
  title: string;
  titleLat: string;
  src: string;
  type: 'before' | 'after';
}

const EXTRA_BEFORE: FrescoSingle[] = [
  { id: 'blagovesti-portal', title: 'Благовести — портал', titleLat: 'Blagovesti — portal', src: '/resource/freskopis/pre/blagovesti-portal.webp', type: 'before' },
  { id: 'bogorodica-blagovesti', title: 'Богородица из Благовести', titleLat: 'Bogorodica iz Blagovesti', src: '/resource/freskopis/pre/bogorodica-blagovesti.webp', type: 'before' },
  { id: 'raspece-detalj-jovan', title: 'Распеће — детаљ, Св. Јован', titleLat: 'Raspeće — detalj, Sv. Jovan', src: '/resource/freskopis/pre/raspece-detalj-jovan.webp', type: 'before' },
  { id: 'raspece-detalj-zene', title: 'Распеће — детаљ, Свете жене', titleLat: 'Raspeće — detalj, Svete žene', src: '/resource/freskopis/pre/raspece-detalj-zene.webp', type: 'before' },
  { id: 'oltar-oplakivanje-hristos', title: 'Олтар — Оплакивање, Христос', titleLat: 'Oltar — Oplakivanje, Hristos', src: '/resource/freskopis/pre/oltar-oplakivanje-hristos.webp', type: 'before' },
  { id: 'oltar-oplakivanje-bogorodica', title: 'Олтар — Оплакивање, Богородица', titleLat: 'Oltar — Oplakivanje, Bogorodica', src: '/resource/freskopis/pre/oltar-oplakivanje-bogorodica.webp', type: 'before' },
  { id: 'prenos-mostiju', title: 'Пренос моштију', titleLat: 'Prenos moštiju', src: '/resource/freskopis/pre/prenos-mostiju.webp', type: 'before' },
  { id: 'priprata-milovanje', title: 'Припрата — Миловање', titleLat: 'Priprata — Milovanje', src: '/resource/freskopis/pre/priprata-milovanje.webp', type: 'before' },
  { id: 'priprata-susret', title: 'Припрата — Сусрет Јоакима и Ане', titleLat: 'Priprata — Susret Joakima i Ane', src: '/resource/freskopis/pre/priprata-susret.webp', type: 'before' },
  { id: 'priprata-rodjenje', title: 'Припрата — Рођење Богородице', titleLat: 'Priprata — Rođenje Bogorodice', src: '/resource/freskopis/pre/priprata-rodjenje.webp', type: 'before' },
  { id: 'priprata-ulazak', title: 'Припрата — Улазак у Храм', titleLat: 'Priprata — Ulazak u Hram', src: '/resource/freskopis/pre/priprata-ulazak.webp', type: 'before' },
  { id: 'priprata-molitva', title: 'Припрата — Молитва Св. Ане', titleLat: 'Priprata — Molitva Sv. Ane', src: '/resource/freskopis/pre/priprata-molitva-ane.webp', type: 'before' },
  { id: 'priprata-blagoslov', title: 'Припрата — Благослов', titleLat: 'Priprata — Blagoslov', src: '/resource/freskopis/pre/priprata-blagoslov.webp', type: 'before' },
  { id: 'raspece-zapadni-zid', title: 'Распеће', titleLat: 'Raspeće', src: '/resource/freskopis/pre/raspece.webp', type: 'before' },
  { id: 'raspece-detalj', title: 'Распеће — детаљ', titleLat: 'Raspeće — detalj', src: '/resource/freskopis/pre/raspece-wide.webp', type: 'before' },
  { id: 'ktitorska-detalj-pre', title: 'Ктиторска композиција — детаљ', titleLat: 'Ktitorska kompozicija — detalj', src: '/resource/freskopis/pre/ktitorska-detalj.webp', type: 'before' },
  { id: 'ktitorska-detalj-2-pre', title: 'Ктиторска композиција — детаљ фреске', titleLat: 'Ktitorska kompozicija — detalj freske', src: '/resource/freskopis/pre/ktitorska-detalj-2.webp', type: 'before' },
];

const EXTRA_AFTER: FrescoSingle[] = [
  { id: 'begstvo', title: 'Бегство у Египат', titleLat: 'Begstvo u Egipat', src: '/resource/freskopis/posle/begstvo-u-egipat.webp', type: 'after' },
  { id: 'vaskrsenje', title: 'Васкрсење Лазарево', titleLat: 'Vaskrsenje Lazarevo', src: '/resource/freskopis/posle/vaskrsenje-lazarevo.webp', type: 'after' },
  { id: 'vaskrsenje-detalj', title: 'Васкрсење Лазарево — детаљ', titleLat: 'Vaskrsenje Lazarevo — detalj', src: '/resource/freskopis/posle/vaskrsenje-lazarevo-detalj.webp', type: 'after' },
  { id: 'svirac', title: 'Свирач у двојнице', titleLat: 'Svirač u dvojnice', src: '/resource/freskopis/posle/svirac.webp', type: 'after' },
  { id: 'proroci', title: 'Пророци', titleLat: 'Proroci', src: '/resource/freskopis/posle/proroci.webp', type: 'after' },
  { id: 'uspenje-posle', title: 'Успење Пресвете Богородице', titleLat: 'Uspenje Presvete Bogorodice', src: '/resource/freskopis/posle/uspenje.webp', type: 'after' },
  { id: 'ktitorska-detalj-posle', title: 'Ктиторска композиција — детаљ', titleLat: 'Ktitorska kompozicija — detalj', src: '/resource/freskopis/posle/ktitorska-detalj.webp', type: 'after' },
  { id: 'rodjenje', title: 'Рођење Христово', titleLat: 'Rođenje Hristovo', src: '/resource/freskopis/posle/rodjenje-hristovo.webp', type: 'after' },
];

/* ── Text paragraphs ─────────────────────────────────────────── */
const TEXT_PARAGRAPHS = [
  {
    cyr: 'Све фреске Богородичине цркве настале су у исто време. Нажалост, градачки фрескопис великим делом је оштећен у току дугог периода када је црква била откривена и напуштена.',
    lat: 'Sve freske Bogorodičine crkve nastale su u isto vreme. Nažalost, gradački freskopis velikim delom je oštećen u toku dugog perioda kada je crkva bila otkrivena i napuštena.',
  },
  {
    cyr: 'Најбоље је сачувана фреска Благовести која се налази у линети главног портала.',
    lat: 'Najbolje je sačuvana freska Blagovesti koja se nalazi u lineti glavnog portala.',
  },
  {
    cyr: 'Зидови припрате били су украшени циклусом сцена из живота Пресвете Богородице. Композиција Благовести била је насликана на источном зиду припрате, али сада се на том месту може видети само крило архангела и део ореола Богородице. Преостале композиције овог циклуса су: Анина молитва у врту, Сусрет Јоакима и Ане, Рођење Богородице и Миловање, Одбијање дарова, Благослов свештеника и Ваведење.',
    lat: 'Zidovi priprate bili su ukrašeni ciklusom scena iz života Presvete Bogorodice. Kompozicija Blagovesti bila je naslikana na istočnom zidu priprate, ali sada se na tom mestu može videti samo krilo arhanđela i deo oreola Bogorodice. Preostale kompozicije ovog ciklusa su: Anina molitva u vrtu, Susret Joakima i Ane, Rođenje Bogorodice i Milovanje, Odbijanje darova, Blagoslov sveštenika i Vavedenje.',
  },
  {
    cyr: 'Јужна капела Богородичине цркве посвећена је светом Симеону Немањи и у њој је био насликан живот овог светитеља од одласка у Свету Гору до смрти и преноса његових моштију у Студеницу.',
    lat: 'Južna kapela Bogorodičine crkve posvećena je svetom Simeonu Nemanji i u njoj je bio naslikan život ovog svetitelja od odlaska u Svetu Goru do smrti i prenosa njegovih moštiju u Studenicu.',
  },
  {
    cyr: 'Између фресака на јужној и северној страни наоса постигнут је посебан сликарски склад. На северном зиду, у оквиру композиције Успења Пресвете Богородице, представљено је како Христос прихвата Богородицу као тек рођену бебу у своје наручје. Наспрам те фреске, на јужном зиду наоса, налази се композиција Рођења и детињства Христовог, на којој је осликано како Пресвета Богородица држи малог Христа у свом наручју, док са праведним Јосифом путује за Египат.',
    lat: 'Između fresaka na južnoj i severnoj strani naosa postignut je poseban slikarski sklad. Na severnom zidu, u okviru kompozicije Uspenja Presvete Bogorodice, predstavljeno je kako Hristos prihvata Bogorodicu kao tek rođenu bebu u svoje naručje. Naspram te freske, na južnom zidu naosa, nalazi se kompozicija Rođenja i detinjstva Hristovog, na kojoj je oslikano kako Presveta Bogorodica drži malog Hrista u svom naručju, dok sa pravednim Josifom putuje za Egipat.',
  },
  {
    cyr: 'Овај необични паралелизам на поменутим фрескама рефлектује чудесни Божји домострој спасења и дубоко уметничко промишљање градачких фрескописаца и ктиторке о том Божанском уређењу.',
    lat: 'Ovaj neobični paralelizam na pomenutim freskama reflektuje čudesni Božji domostoj spasenja i duboko umetničko promišljanje gradačkih freskopisaca i ktitorke o tom Božanskom uređenju.',
  },
  {
    cyr: 'Ктиторска композиција се у немањићким задужбинама налази на југозападном зиду наоса, изнад гробнице ктитора. На ктиторској композицији градачке цркве Пресвета Богородица и свети краљ Стефан Првовенчани заступају ктиторски пар пред Господом Исусом Христом. Композиција се наставља приказом фигура краља Драгутина, краља Милутина и Драгутинове жене Кателине.',
    lat: 'Ktitorska kompozicija se u nemanjićkim zadužbinama nalazi na jugozapadnom zidu naosa, iznad grobnice ktitora. Na ktitorskoj kompoziciji gradačke crkve Presveta Bogorodica i sveti kralj Stefan Prvovenčani zastupaju ktitorski par pred Gospodom Isusom Hristom. Kompozicija se nastavlja prikazom figura kralja Dragutina, kralja Milutina i Dragutinove žene Kateline.',
  },
  {
    cyr: 'Изнад јужне певнице налази се фигура пророка Гедеона, која је добро очувана. На златној позадини ове фреске исцртани су квадратићи како би се постигао изглед мозаика. Сматра се су тако изгледале позадине свих фресака у овој цркви.',
    lat: 'Iznad južne pevnice nalazi se figura proroka Gedeona, koja je dobro očuvana. Na zlatnoj pozadini ove freske iscrtani su kvadratići kako bi se postigao izgled mozaika. Smatra se su tako izgledale pozadine svih fresaka u ovoj crkvi.',
  },
  {
    cyr: 'Фигура Богородице у проскомидији и фигура мртвог Христа у ђаконикону део су јединствене сцене Оплакивања, насликане по први пут на зиду управо у оквиру градачког фрескописа.',
    lat: 'Figura Bogorodice u proskomidiji i figura mrtvog Hrista u đakonikonu deo su jedinstvene scene Oplakivanja, naslikane po prvi put na zidu upravo u okviru gradačkog freskopisa.',
  },
  {
    cyr: 'Црква Светог Николе фрескописана је вероватно у XIV веку. Фреске су делимично сачуване. Велико попрсје Светог Николе, којег је краљица Јелена посебно поштовала, насликано је на јужној страни, а најбоље је очувана фигура анђела у олтару.',
    lat: 'Crkva Svetog Nikole freskopisana je verovatno u XIV veku. Freske su delimično sačuvane. Veliko poprsje Svetog Nikole, kojeg je kraljica Jelena posebno poštovala, naslikano je na južnoj strani, a najbolje je očuvana figura anđela u oltaru.',
  },
];

/* ── Comparison Slider Component ──────────────────────────────── */
const ComparisonSlider: React.FC<{
  before: string;
  after: string;
  title: string;
  onImageClick: (src: string, alt: string) => void;
}> = ({ before, after, title, onImageClick }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);

  const updatePosition = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setPosition(pct);
  }, []);

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => { e.preventDefault(); updatePosition(e.clientX); };
    const onTouchMove = (e: TouchEvent) => { updatePosition(e.touches[0].clientX); };
    const onUp = () => setIsDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onTouchMove);
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [isDragging, updatePosition]);

  return (
    <div
      ref={containerRef}
      className="frescoes-slider"
      onMouseDown={(e) => { setIsDragging(true); updatePosition(e.clientX); }}
      onTouchStart={(e) => { setIsDragging(true); updatePosition(e.touches[0].clientX); }}
    >
      {/* After image (full background) */}
      <img
        src={after}
        alt={`${title} — после рестаурације`}
        className="frescoes-slider-img frescoes-slider-after"
        loading="lazy"
        decoding="async"
        onClick={() => onImageClick(after, `${title} — после рестаурације`)}
      />
      {/* Before image (clipped) */}
      <div
        className="frescoes-slider-before-wrap"
        style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
      >
        <img
          src={before}
          alt={`${title} — пре рестаурације`}
          className="frescoes-slider-img"
          loading="lazy"
          decoding="async"
          onClick={() => onImageClick(before, `${title} — пре рестаурације`)}
        />
      </div>
      {/* Divider line */}
      <div className="frescoes-slider-line" style={{ left: `${position}%` }}>
        <div className="frescoes-slider-handle">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M8 5L3 12L8 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16 5L21 12L16 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>
      {/* Labels */}
      <span className="frescoes-slider-label frescoes-slider-label-before">Пре</span>
      <span className="frescoes-slider-label frescoes-slider-label-after">После</span>
    </div>
  );
};

/* ── Main Page Component ──────────────────────────────────────── */
const FrescoesPage: React.FC = () => {
  useDocumentTitle('Фрескопис — Манастир Градац', '/freskopis');
  const { t } = useScript();
  const langFade = useLangFade();

  // Lightbox
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [lightboxAlt, setLightboxAlt] = useState('');

  const openLightbox = (src: string, alt: string) => {
    setLightboxSrc(src);
    setLightboxAlt(alt);
  };

  const closeLightbox = () => {
    setLightboxSrc(null);
    setLightboxAlt('');
  };

  useEffect(() => {
    if (!lightboxSrc) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [lightboxSrc]);

  // Protect images: disable contextmenu and dragstart on all images in this page
  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'IMG') {
        e.preventDefault();
      }
    };
    const handleDragStart = (e: DragEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'IMG') {
        e.preventDefault();
      }
    };
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('dragstart', handleDragStart);
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('dragstart', handleDragStart);
    };
  }, []);

  const handleImgError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.style.display = 'none';
  };

  return (
    <div className="frescoes-page">
      {/* Breadcrumb */}
      <nav className="category-breadcrumb" aria-label="Breadcrumb">
        <Link to="/">{t('Почетна')}</Link>
        <span className="breadcrumb-sep">›</span>
        <span>{t('Фрескопис')}</span>
      </nav>

      {/* Hero */}
      <header className="frescoes-hero">
        <h1 className="frescoes-hero-title">{t('Фрескопис')}</h1>
        <p className="frescoes-hero-subtitle">
          {t('Фреске Богородичине цркве — ремек-дела српског средњовековног сликарства XIII века')}
        </p>
      </header>

      {/* Text Section */}
      <section className={`frescoes-text ${langFade}`}>
        <div className="frescoes-text-inner">
          <div className="frescoes-text-dropcap">
            {TEXT_PARAGRAPHS.map((para, i) => (
              <p key={i} className={i === 0 ? 'frescoes-first-para' : ''}>
                {t(para.cyr)}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="frescoes-section-divider">
        <span className="frescoes-divider-icon">✦</span>
        <span className="frescoes-divider-text">{t('Пре и после рестаурације')}</span>
        <span className="frescoes-divider-icon">✦</span>
      </div>

      {/* Comparison Gallery */}
      <section className={`frescoes-comparison ${langFade}`}>
        <p className="frescoes-comparison-intro">
          {t('Превуците клизач лево-десно да упоредите изглед фресака пре и после рестаурације')}
        </p>
        <div className="frescoes-comparison-grid">
          {FRESCO_PAIRS.map((pair) => (
            <article key={pair.id} className="frescoes-card">
              <ComparisonSlider
                before={pair.before}
                after={pair.after}
                title={t(pair.title)}
                onImageClick={openLightbox}
              />
              <div className="frescoes-card-body">
                <h3 className="frescoes-card-title">{t(pair.title)}</h3>
                {pair.description && (
                  <p className="frescoes-card-desc">{t(pair.description)}</p>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Extra Before Images */}
      <div className="frescoes-section-divider">
        <span className="frescoes-divider-icon">✦</span>
        <span className="frescoes-divider-text">{t('Фреске пре рестаурације')}</span>
        <span className="frescoes-divider-icon">✦</span>
      </div>

      <section className={`frescoes-extra ${langFade}`}>
        <div className="frescoes-extra-grid">
          {EXTRA_BEFORE.map((img) => (
            <article key={img.id} className="frescoes-extra-card">
              <button
                className="frescoes-extra-btn"
                onClick={() => openLightbox(img.src, t(img.title))}
              >
                <img
                  src={img.src}
                  alt={t(img.title)}
                  onError={handleImgError}
                  loading="lazy"
                  decoding="async"
                />
                <div className="frescoes-extra-overlay">
                  <span className="frescoes-extra-zoom">🔍</span>
                </div>
              </button>
              <p className="frescoes-extra-caption">{t(img.title)}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Extra After Images */}
      <div className="frescoes-section-divider">
        <span className="frescoes-divider-icon">✦</span>
        <span className="frescoes-divider-text">{t('Фреске после рестаурације')}</span>
        <span className="frescoes-divider-icon">✦</span>
      </div>

      <section className={`frescoes-extra ${langFade}`}>
        <div className="frescoes-extra-grid">
          {EXTRA_AFTER.map((img) => (
            <article key={img.id} className="frescoes-extra-card">
              <button
                className="frescoes-extra-btn"
                onClick={() => openLightbox(img.src, t(img.title))}
              >
                <img
                  src={img.src}
                  alt={t(img.title)}
                  onError={handleImgError}
                  loading="lazy"
                  decoding="async"
                />
                <div className="frescoes-extra-overlay">
                  <span className="frescoes-extra-zoom">🔍</span>
                </div>
              </button>
              <p className="frescoes-extra-caption">{t(img.title)}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Lightbox */}
      {lightboxSrc && (
        <div
          className="lightbox"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label={t('Преглед слике')}
        >
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox} aria-label={t('Затвори')}>✕</button>
            <img src={lightboxSrc} alt={lightboxAlt} />
            {lightboxAlt && <p className="lightbox-caption">{lightboxAlt}</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default FrescoesPage;
